import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useSafe } from '../../context/SafeContext';
import LoadingPane from '../../components/LoadingPane';
import ContactForm, { type ContactFormValues } from '../../components/ContactForm';

/**
 * Edit a family-shared contact. Reuses the personal `ContactForm` UI but saves
 * back to the ORG# item via `updateItem` (identity-preserving; the sync path
 * re-encrypts under the same id, so the server sees an update). Any family
 * member may edit — the server gate allows member edits of contact items.
 */
export default function FamilyContactEditorPage(): JSX.Element {
  const { orgId, categoryId, itemId } = useParams<{ orgId: string; categoryId?: string; itemId: string }>();
  const navigate = useNavigate();
  const { items, itemsLoaded, updateItem, sync } = useSafe();
  const existing = items.find((i) => i.id === itemId && i.orgId === orgId && i.type === 'contact');
  const [loading, setLoading] = useState(false);

  const detailTo = categoryId
    ? `/orgs/${orgId}/binder/${categoryId}/contacts/${itemId}`
    : `/orgs/${orgId}/binder/contacts/${itemId}`;

  if (!existing) {
    // Note: the `loading` state above is the SUBMIT spinner, not a data-load
    // flag — items is emptied on lock, so absence means loading until
    // itemsLoaded says the sync landed.
    if (!itemsLoaded) return <LoadingPane label="Loading contact…" className="p-7 text-hbs-text-mute" />;
    return (
      <div className="p-7 text-hbs-text-mute">
        Contact not found. <button id="family-contact-editor-not-found-back-button" className="text-hbs-primary hover:underline" onClick={() => navigate(detailTo)}>Back</button>
      </div>
    );
  }

  async function handleSubmit(values: ContactFormValues): Promise<void> {
    setLoading(true);
    try {
      await updateItem(existing!.id, { type: 'contact', ...values });
      await sync();
      navigate(detailTo);
    } finally { setLoading(false); }
  }

  return (
    <ContactForm
      // Remount per contact so the useState field seeds re-run — guards
      // against a prior contact's data lingering if the editor is reused
      // across a contact switch (same reason as ContactEditorPage).
      key={itemId}
      heading={`Edit ${existing.name}`}
      initial={existing} isEdit loading={loading}
      onSubmit={handleSubmit}
      onCancel={() => navigate(detailTo)}
    />
  );
}
